import validator from 'validator';

/**
 * Validates that a string is not empty, null, or undefined.
 *
 * @param {string} str - The string to be validated.
 * @returns {object} An object containing:
 *   @property {boolean} isValid - Indicates whether the string is valid.
 *   @property {string|null} error - An error message if the validation fails; otherwise, null.
 */
export function validateNotEmpty(str) {
  let result = {
    isValid: true,
    error: null,
  };

  if (str === null || str === undefined || (typeof str === 'string' && str.trim().length === 0)) {
    result.isValid = false;
    result.error = 'This field is required.';
  }

  return result;
};

/**
 * Validates the length of a string between two integers.
 *
 * @param {string} string - The string to be validated.
 * @param {number} min - The minimum length for the string.
 * @param {number} max - The maximum length for the string.
 * @param {string} [stringType] - An optional description for the string type, used in error messages.
 * @returns {object} An object containing:
 *   @property {boolean} isValid - Indicates whether the string length is within the specified limits.
 *   @property {string|null} error - An error message if the validation fails; otherwise, null.
 */
export function validateStringLength(string, min, max, stringType) {
  let result = {
    isValid: true,
    error: null,
  };

  const length = string.trim().length;

  if (length < min) {
    result.isValid = false;
    result.error = `${stringType || 'Input'} must be more than ${min} in length.`;
  } else if (length > max) {
    result.isValid = false;
    result.error = `${stringType || 'Input'} exceeded maximum length of ${max}.`;
  }

  return result;
};

/**
 * Validates that an integer is within specified minimum and maximum bounds.
 *
 * @param {number|string|null|undefined} int - The integer to be validated
 * @param {number} min - The minimum acceptable value for the integer.
 * @param {number} max - The maximum acceptable value for the integer.
 * @param {string} [intType] - An optional description for the integer type, used in error messages.
 * @returns {object} An object containing:
 *   @property {boolean} isValid - Indicates whether the integer is within the specified bounds.
 *   @property {string|null} error - An error message if the validation fails; otherwise, null.
 */
export function validateIntBounds(int, min, max, intType) {
  let result = {
    isValid: true,
    error: null,
  };

  const numInt = Number(int);

  if (int === undefined || int === null || int === '') {
    result.isValid = false;
    result.error = `${intType || 'Input'} must be a number.`;
  } else if (isNaN(numInt)) {
    result.isValid = false;
    result.error = `Please provide a value between ${min} and ${max}.`;
  } else if (numInt < min) {
    result.isValid = false;
    result.error = `The minimum value for ${intType || 'this field'} is ${min}.`;
  } else if (numInt > max) {
    result.isValid = false;
    result.error = `The maximum value for ${intType || 'this field'} is ${max}.`;
  }

  return result;
};

/**
 * Validates whether a string is a valid IP address of a specified version.
 *
 * @param {string} str - The string to be validated as an IP address.
 * @param {number} version - The IP version to validate against (4 or 6).
 * @param {string} [stringType] - An optional description for the string type, used in error messages.
 * @returns {object} An object containing:
 *   @property {boolean} isValid - Indicates whether the string is a valid IP address of the specified version.
 *   @property {string|null} error - An error message if validation fails; otherwise, null.
 */
export function validateIP(str, version, stringType) {
  let result = {
    isValid: Boolean(typeof str === 'string' && validator.isIP(str, version)),
    error: null,
  };

  if (!result.isValid) {
    result.error = `${stringType || 'Network address'} must be an ipv${version} address.`;
  }

  return result;
}

/**
 * Validates whether a string is a valid domain name (FQDN).
 *
 * @param {string} str - The string to be validated as a domain name.
 *
 * @returns {object} An object containing:
 *   @property {boolean} isValid - Indicates whether the string is a valid domain name.
 *   @property {string|null} error - An error message if validation fails; otherwise, null.
 */
export function validateDomain(str) {
  let result = {
    isValid: Boolean(typeof str === 'string' && validator.isFQDN(str)),
    error: null,
  };

  if (!result.isValid) {
    result.error = 'Network address must be a valid domain name.';
  }

  return result;
}

/**
 * Validates whether a string is either a valid IP address of a specified version
 * or a valid fully qualified domain name (FQDN).
 *
 * @param {string} str - The string to be validated as either an IP address or a FQDN.
 * @param {number} version - The IP version to validate against (4 or 6).
 * @param {string} label - Changes the error message based on label arg. Default is "Network"
 *
 * @returns {object} An object containing:
 *   @property {boolean} isValid - Indicates whether the string is a valid IP address or a valid FQDN.
 *   @property {string|null} error - An error message if validation fails; otherwise, null.
 */
export function validateIPOrFQDN(str, version, label) {
  const isValidIP = validateIP(str, version).isValid;
  const isValidDomain = validateDomain(str).isValid;

  let result = {
    isValid: Boolean(typeof str === 'string' && (isValidIP || isValidDomain)),
    error: null,
  };

  if (!result.isValid) {
    result.error =
      `${label ?? 'Network'} address must be an IPv${version} address or FQDN (Fully Qualified Domain Name).`;
  }

  return result;
}

/**
 *
 * @param {*} str
 * @returns obj containing error state and error message (if any)
 */
export const validateAlphaNumeric = (str) => {
  const regex = /^[a-zA-Z0-9-_]+$/;

  if (str.length > 0 && validator.matches(str, regex)) {
    return {
      isValid: true,
      error: null,
    };
  } else {
    return {
      isValid: false,
      error: 'Name may only contain alpha-numeric characters, underscores, and dashes.',
    };
  }
};

/**
* Validates whether a string is a properly formatted email address.
*
* @param {string} str - The string to be validated as an email address.
* @returns {object} An object containing:
*   @property {boolean} isValid - Indicates whether the string is a valid email address.
*   @property {string|null} error - An error message if validation fails; otherwise, null.
*/
export const validateEmail = (str) => {
  const result = {
    isValid: true,
    error: null,
  };

  if (str.length > 0 && !validator.isEmail(str, {
    allow_underscores: true,
  })) {
    result.isValid = false;
    result.error = 'Email must be in the format name@example.com.';
  }

  return result;
};


/**
* Validates whether a string is a properly formatted phone number.
*
* @param {string} str - The string to be validated as a phone number.
* @returns {object} An object containing:
*   @property {boolean} isValid - Indicates whether the string is a valid phone number.
*   @property {string|null} error - An error message if validation fails; otherwise, null.
*/
export const validatePhoneNumber = (str) => {
  const result = {
    isValid: true,
    error: null,
  };

  // Remove spaces and hyphens
  const sanitizedStr = str.replace(/[-\s]/g, '');

  // Check if the sanitized string contains only digits
  if (str.length > 0 && !/^\d+$/.test(sanitizedStr)) {
    result.isValid = false;
    result.error = 'Phone number must be in digits.';
  }

  return result;
};

/**
 * Validates the name field for a stream: required, max 32 chars, alphanumeric/dash/underscore only.
 * Duplicate-name checks are performed asynchronously and are not included here.
 *
 * @param {string} name
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateStreamName(name) {
  if (name.trim().length === 0) return { isValid: false, error: null };
  if (name.trim().length > 32) return { isValid: false, error: 'Name exceeded maximum length of 32.' };
  return validateAlphaNumeric(name);
}

/**
 * Validates the description field for a stream: max 140 chars.
 *
 * @param {string} description
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateStreamDescription(description) {
  if (description.trim().length > 140) {
    return { isValid: false, error: 'Description exceeded maximum length of 140.' };
  }
  return { isValid: true, error: null };
}
